import { useState } from "react";
import { sendVerificationEmail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";
import { toast } from "sonner";
import { Mail, X } from "lucide-react";

/** Banner-only enforcement: nudges unverified email/password users to verify,
 * but never blocks features. Hidden for verified users, Google users, and when
 * dismissed via localStorage (per-session). */
export default function VerifyEmailBanner() {
    const { user } = useAuth();
    const [sending, setSending] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [dismissed, setDismissed] = useState(() => {
        try {
            return sessionStorage.getItem("bl_verify_banner_dismissed") === "1";
        } catch {
            return false;
        }
    });

    if (!user) return null;
    if (user.email_verified) return null;
    if (dismissed) return null;
    // Google OAuth users are auto-verified at the provider; show nothing.
    if (user.auth_provider !== "email") return null;

    const onResend = async () => {
        if (sending || cooldown > 0) return;
        setSending(true);
        try {
            await sendVerificationEmail();
            track("email_verification_resent");
            toast.success("Verification email sent. Check your inbox.");
            // 60s cooldown to mirror the backend throttle window
            let s = 60;
            setCooldown(s);
            const id = setInterval(() => {
                s -= 1;
                setCooldown(s);
                if (s <= 0) clearInterval(id);
            }, 1000);
        } catch (err) {
            toast.error(
                err?.response?.data?.detail ||
                    "Could not send verification email. Please try again.",
            );
        } finally {
            setSending(false);
        }
    };

    const dismiss = () => {
        setDismissed(true);
        try {
            sessionStorage.setItem("bl_verify_banner_dismissed", "1");
        } catch { /* non-critical */ }
    };

    return (
        <div
            className="bl-container pt-4"
            data-testid="verify-email-banner"
        >
            <div className="flex items-start gap-3 rounded-lg border border-bl bg-bl-surface px-4 py-3">
                <Mail
                    className="w-5 h-5 text-bl-primary flex-shrink-0 mt-0.5"
                    aria-hidden
                />
                <div className="flex-1 min-w-0">
                    <p className="text-sm text-bl">
                        <span className="font-medium">
                            Verify your email
                        </span>{" "}
                        <span className="text-bl-muted">
                            — we sent a link to{" "}
                            <span className="text-bl">{user.email}</span>.
                            Verifying keeps your account secure.
                        </span>
                    </p>
                </div>
                <button
                    onClick={onResend}
                    disabled={sending || cooldown > 0}
                    className="text-sm text-bl-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    data-testid="verify-banner-resend-btn"
                >
                    {cooldown > 0
                        ? `Resend in ${cooldown}s`
                        : sending
                          ? "Sending…"
                          : "Resend"}
                </button>
                <button
                    onClick={dismiss}
                    className="text-bl-muted hover:text-bl"
                    aria-label="Dismiss"
                    data-testid="verify-banner-dismiss-btn"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
